import { useLazyQuery } from "@apollo/client";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import { gqlRecognition } from "gql";
import { IRecognitionByUserPaginated } from "gql/Recognitions/queries";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Card, Loader, LiquidPill, PageEmptyState, Pagination, resolveCorporateValueVisual } from "components/ui/v2";
import { ERecognitionType } from "types/CorporateValue.type";

interface RecognitionByCorporateValueProps {
  readonly corporateValueId: string;
  readonly corporateValueName: string;
  readonly corporateValueIcon: string;
  readonly quantity: string | number;
  readonly recognitionType: string;
  userId?: string;
}
const pageSize = 10;

export default function RecognitionByCorporateValue(
  prop: RecognitionByCorporateValueProps
) {
  const {
    corporateValueId,
    corporateValueName,
    corporateValueIcon,
    quantity,
    recognitionType,
    userId = "",
  } = prop;

  const { t } = useTranslation("common");
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  const [
    getRecognitionsPaginated,
    { loading: recognitionPerUserLoading, data: recognitionPerUser },
  ] = useLazyQuery<IRecognitionByUserPaginated>(
    gqlRecognition.queries.GET_RECOGNITIONS_BY_CORPORATE_VALUE_PAGINATED,
    {
      variables: {
        corporateValueByUserPaginated: {
          corporateValueId: corporateValueId,
          recognitionType: recognitionType,
          skip: 0,
          limit: 10,
          userId,
        },
      },
      onCompleted: (data) => {
        setTotal(data.getRecognitionByUser.total);
      },
      fetchPolicy: "network-only",
    }
  );

  const handleChangePage = async (currentPage: number) => {
    const response = await getRecognitionsPaginated({
      variables: {
        corporateValueByUserPaginated: {
          corporateValueId: corporateValueId,
          recognitionType: recognitionType,
          skip: currentPage - 1,
          limit: pageSize,
          userId,
        },
      },
    });
    if (response) {
      setPage(currentPage);
    }
  };

  useEffect(() => {
    getRecognitionsPaginated({
      variables: {
        corporateValueByUserPaginated: {
          corporateValueId: corporateValueId,
          recognitionType: recognitionType,
          skip: (page - 1) * pageSize,
          limit: pageSize,
          userId,
        },
      },
    });
  }, []);

  const { variant: cvVariant } = resolveCorporateValueVisual(corporateValueName);

  const content =
    recognitionPerUser?.getRecognitionByUser?.userRecognitions &&
    recognitionPerUser.getRecognitionByUser.userRecognitions.length > 0 ? (
      <div className="container">
        <div className="flex justify-self-start">
          <span className="mb-1 mt-4 flex text-left text-base font-bold text-slate-900 dark:text-white">
            {t(
              recognitionType === ERecognitionType.RECEIVING
                ? "titulo reconocimientos recibidos"
                : "titulo reconocimientos enviados",
              {
                corporateValue: corporateValueName.toLowerCase(),
              }
            )}
          </span>
        </div>
        <Card size="full" padding="comfortable">
          <div className="grid grid-cols-[40px,1fr,1fr] content-start items-center gap-2">
            <div className="relative grid h-[40px] w-[40px] overflow-hidden rounded-full">
              <Image
                src={corporateValueIcon}
                alt="corporateValueIcon"
                sizes="(max-width: 768px) 80vw, (min-width: 769px) 25vw"
                fill
                className="h-10 w-10 rounded-full"
              />
            </div>
            <div className="text-sm text-slate-700 dark:text-zinc-300">
              {t("Este es el valor de", {
                corporateValue: corporateValueName.toLowerCase(),
              })}
            </div>
            <div>
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm text-slate-600 dark:text-zinc-400">
                  {t(
                    ERecognitionType.RECEIVING === recognitionType
                      ? "Recibidos"
                      : "Enviados"
                  )}
                </span>
                <LiquidPill
                  variant={cvVariant}
                  appearance="tinted"
                  shape="rounded"
                  size="md"
                  text={String(quantity)}
                />
              </div>
            </div>
          </div>
        </Card>
        <div className="mt-3 flex flex-col gap-3">
          {recognitionPerUser.getRecognitionByUser.userRecognitions.map(
            (recognition) => (
              <Card
                key={recognition.recognitionId}
                size="full"
                padding="comfortable"
              >
                <div className="grid grid-cols-1 justify-between lg:grid-cols-2">
                  <div className="grid items-start justify-start text-wrap">
                    <AvatarWithInitials
                      urlImg={recognition.profileImage}
                      initials={`${recognition.name ?? ""} ${recognition.lastname ?? ""}`}
                      title={`${recognition.name ?? ""} ${recognition.lastname ?? ""}`}
                      subtitle={recognition.email ?? ""}
                    />
                  </div>
                  <div className="grid justify-start lg:justify-end">
                    <span className="text-xs text-slate-500 dark:text-zinc-400">
                      {recognition.recognitionDate}
                    </span>
                  </div>
                </div>
                <div className="ml-1 mt-1 flex text-sm text-slate-700 dark:text-zinc-300">
                  &quot;{recognition.recognitionMessage}&quot;
                </div>
              </Card>
            )
          )}
        </div>
        <div className="mt-4 flex justify-center">
          <Pagination
            activePage={page}
            total={total}
            limit={pageSize}
            onChangePage={handleChangePage}
            prev
            next
            ellipsis
          />
        </div>
      </div>
    ) : (
      <PageEmptyState
        title={t("No existe información para mostrar")}
        description={t(
          ERecognitionType.RECEIVING === recognitionType
            ? "Espera a recibir reconocimientos para este valor corporativo"
            : "Aun no compartes reconocimientos para este valor corporativo"
        )}
      />
    );

  return (
    <div className="overflow-y-auto px-6">
      {recognitionPerUserLoading ? (
        <div className="flex w-full flex-col items-center justify-center">
          <Loader text={t("Espere por favor")} />
        </div>
      ) : (
        content
      )}
    </div>
  );
}
